<!-- eslint-disable vue/no-use-v-if-with-v-for -->
<script setup lang="ts">
import { DayJsKey, Item } from '@send-frontend/types';
import { computed, inject, onBeforeMount, ref, watch } from 'vue';

import useFolderStore from '@send-frontend/apps/send/stores/folder-store';
import { useStatusStore } from '@send-frontend/apps/send/stores/status-store';
import { useFolderQuery } from '@send-frontend/lib/queries/folderQueries';
import '@thunderbirdops/services-ui/style.css';

import LoadingComponent from '@send-frontend/apps/common/LoadingComponent.vue';
import DeleteModal from '@send-frontend/apps/common/modals/DeleteModal.vue';
import DownloadModal from '@send-frontend/apps/common/modals/DownloadModal.vue';
import SendIconBlack from '@send-frontend/apps/common/SendIconBlack.vue';
import BreadCrumb from '@send-frontend/apps/send/components/BreadCrumb.vue';
import { default as Btn } from '@send-frontend/apps/send/elements/BtnComponent.vue';
import FolderTableRowCell from '@send-frontend/apps/send/elements/FolderTableRowCell.vue';
import {
  computeMultipartFile,
  handleMultipartDownload,
} from '@send-frontend/lib/folderView';
import { useIsMobile } from '@send-frontend/composables/useIsMobile';
import { useApiStore, useKeychainStore } from '@send-frontend/stores';
import { IconDotsVertical, IconDownload, IconTrash } from '@tabler/icons-vue';
import { ExpiryBadge, ExpiryUnitTypes } from '@thunderbirdops/services-ui';
import { useDebounceFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useModal, useModalSlot } from 'vue-final-modal';
import { useRoute, useRouter } from 'vue-router';
import { ItemResponse } from '../stores/folder-store.types';
import DeleteConfirmation from './DeleteConfirmation.vue';
import DownloadConfirmation from './DownloadConfirmation.vue';

const folderStore = useFolderStore();
const statusStore = useStatusStore();
const { isRouterLoading } = storeToRefs(statusStore);
const { api } = useApiStore();
const { keychain } = useKeychainStore();

const dayjs = inject(DayJsKey);
const selectedFolder = ref<string | null>(null);

const route = useRoute();
const router = useRouter();
const selectedFile = ref<Item>();

const deleteItemRef = ref<{
  id: string | number;
  name: string;
  type: 'folder' | 'file';
}>();

const filesInFolder = computed(() => {
  return folderStore.rootFolder?.items;
});

const multipartFile = computed(() => {
  return computeMultipartFile(
    folderStore.selectedFile.wrappedKey,
    folderStore.rootFolder.items
  );
});

const onDownloadConfirm = () => {
  const { id, uploadId, wrappedKey, name, containerId, multipart } =
    selectedFile.value;

  if (multipart) {
    folderStore.setSelectedFile(id);
    return handleMultipartDownload(
      multipartFile.value,
      selectedFile.value,
      folderStore.downloadMultipart,
      api,
      keychain,
      statusStore.progress
    );
  }

  return folderStore.downloadContent(uploadId, containerId, wrappedKey, name);
};

const onDeleteConfirm = async () => {
  if (deleteItemRef.value?.type === 'folder') {
    return await folderStore.deleteFolder(deleteItemRef.value.id as string);
  } else {
    return await folderStore.deleteItem(
      deleteItemRef.value.id as number,
      folderStore.rootFolder.id
    );
  }
};

const { open, close: closefn } = useModal({
  component: DownloadModal,
  slots: {
    default: useModalSlot({
      component: DownloadConfirmation,
      attrs: {
        closefn: () => closefn(),
        confirm: onDownloadConfirm,
        class: 'relative',
      },
    }),
  },
});

const { open: openDeleteModal, close: closeDeleteModal } = useModal({
  component: DeleteModal,
  attrs: {
    title: 'Delete Item?',
  },
  slots: {
    default: useModalSlot({
      component: DeleteConfirmation,
      attrs: {
        closefn: () => closeDeleteModal(),
        confirm: onDeleteConfirm,
        get itemName() {
          return deleteItemRef.value?.name || '';
        },
        get itemType() {
          return deleteItemRef.value?.type || 'file';
        },
      },
    }),
  },
});

// Clicking a row selects it, which mounts the 16rem details sidebar (#903) and
// so reflows the table under the cursor. The second click of a double click is
// then dispatched at coordinates now covered by a different element — commonly
// a row action button that just slid into place, including delete. Ignore any
// click that the browser counted as part of a multi-click sequence: `detail`
// uses the platform's own multi-click window, so this tracks the OS
// double-click interval rather than guessing at it.
const isStrayMultiClick = (event: MouseEvent) => event.detail > 1;

const openModal = (event: MouseEvent, item: ItemResponse) => {
  if (isStrayMultiClick(event)) return;
  selectedFile.value = item;
  open();
};

const openDeleteConfirmation = (
  event: MouseEvent,
  id: string | number,
  name: string,
  type: 'folder' | 'file'
) => {
  if (isStrayMultiClick(event)) return;
  deleteItemRef.value = { id, name, type };
  openDeleteModal();
};

// Use reactive folder ID for the query
const folderId = computed(() => {
  const id = route.params.id as string;
  return id || null;
});

// Use the cached folder query
const folderQuery = useFolderQuery(folderId);

const gotoRoute = useDebounceFn(async () => {
  isRouterLoading.value = true;
  // Refetch the query which will use cached data if available
  await folderQuery.refetch();
  isRouterLoading.value = false;
}, 250);

onBeforeMount(() => {
  gotoRoute();
});

watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId !== oldId) {
      gotoRoute();
    }
  }
);

// To make sure multi part files are loded in memory correctly we refetch after
// the number of files in the folder changes.
watch(filesInFolder, (newValues, OldValues) => {
  if (newValues?.length !== OldValues?.length) {
    // gotoRoute();
  }
});

function handleFileClick(id: number) {
  folderStore.setSelectedFile(id);
}

const isDev = import.meta.env.DEV;

// On mobile we drop the per-row action column (download/delete/⋮) entirely so the
// file list fits the viewport without horizontal scrolling; the actions remain
// reachable from the file/folder info panel (see #977).
const isMobile = useIsMobile();

const isEmpty = computed(() => {
  return (
    !folderStore.visibleFolders?.length &&
    !folderStore.rootFolder?.items?.length
  );
});

function handleFolderClick(uuid: string) {
  if (selectedFolder.value === uuid) {
    // router.push({ name: 'folder', params: { id: uuid } });
    selectedFolder.value = null;
    return;
  }
  folderStore.setSelectedFolder(uuid);
  selectedFolder.value = uuid;
}
</script>

<template>
  <div class="w-full flex flex-col gap-3">
    <h2 class="title">Your Files</h2>
    <span
      v-if="folderStore.rootFolder?.items.length"
      data-testid="file-count"
      style="display: none"
    >
      {{ `${folderStore.rootFolder.items.length}` }}
    </span>
    <span v-else data-testid="empty-folder" style="display: none"
      >This is an empty folder</span
    >
    <BreadCrumb v-if="isDev" />
    <div
      v-if="isRouterLoading || folderQuery.isLoading.value"
      class="inset-0 bg-white/80 z-50 flex items-center justify-center"
    >
      <LoadingComponent />
    </div>
    <div v-else-if="folderQuery.isError.value" class="p-4 text-red-600">
      Error loading folder: {{ folderQuery.error.value?.message }}
      <button
        class="ml-2 px-3 py-1 bg-red-100 hover:bg-red-200 rounded"
        @click="folderQuery.refetch()"
      >
        Retry
      </button>
    </div>
    <div v-else>
      <div
        v-if="isEmpty"
        class="border border-gray-200 rounded-lg flex flex-col items-center justify-center py-16 gap-2 bg-white"
      >
        <div class="rounded-full border-2 border-gray-500 p-3">
          <SendIconBlack class="w-8 h-8 text-gray-500" />
        </div>
        <p class="font-semibold text-gray-700 mt-1">No files</p>
        <p class="text-sm text-gray-500">Uploaded files will appear here.</p>
      </div>
      <table
        v-else
        class="w-full border-separate border-spacing-x-0 border-spacing-y-1"
      >
        <thead>
          <tr>
            <th class="border-r border-b border-gray-300"></th>
            <th class="border-r border-b border-gray-300">Name</th>
            <th v-if="!isMobile" class="border-b border-gray-300"></th>
          </tr>
        </thead>
        <tbody>
          <!-- FOLDERS -->
          <tr
            v-for="folder in folderStore.visibleFolders"
            :key="folder.id"
            class="group"
            data-testid="folder-row"
            @click="handleFolderClick(folder.id)"
            @dblclick="
              router.push({ name: 'folder', params: { id: folder.id } })
            "
          >
            <FolderTableRowCell
              :selected="folder.id === folderStore.selectedFolder?.id"
            >
              <img
                src="@send-frontend/apps/send/assets/folder.svg"
                class="w-8 h-8"
              />
            </FolderTableRowCell>
            <FolderTableRowCell
              :selected="folder.id === folderStore.selectedFolder?.id"
            >
              <router-link
                :to="`/send/folder/${folder.id}`"
                class="max-md:break-all"
              >
                {{ folder.name }}</router-link
              >
              <div class="text-sm">
                Last modified {{ dayjs().to(dayjs(folder.updatedAt)) }}
              </div>
            </FolderTableRowCell>
            <FolderTableRowCell
              v-if="!isMobile"
              :selected="folder.id === folderStore.selectedFolder?.id"
            >
              <div class="flex justify-between">
                <div
                  class="flex gap-2 opacity-0 group-hover:!opacity-100 transition-opacity"
                  :class="{
                    '!opacity-100':
                      folder.id === folderStore.selectedFolder?.id,
                  }"
                >
                  <Btn
                    danger
                    @click.stop="
                      openDeleteConfirmation(
                        $event,
                        folder.id,
                        folder.name,
                        'folder'
                      )
                    "
                  >
                    <IconTrash class="w-4 h-4" />
                  </Btn>
                </div>
                <Btn class="ml-auto">
                  <IconDotsVertical class="w-4 h-4" />
                </Btn>
              </div>
            </FolderTableRowCell>
          </tr>
          <!-- FILES -->
          <template v-if="folderStore.rootFolder">
            <tr
              v-for="(item, index) in folderStore.rootFolder.items"
              :key="item.id"
              class="group cursor-pointer"
              :data-testid="'file-' + index"
              @click="handleFileClick(item.id)"
            >
              <FolderTableRowCell>
                <div class="flex justify-end">
                  <img
                    src="@send-frontend/apps/send/assets/file.svg"
                    class="w-8 h-8"
                  />
                </div>
              </FolderTableRowCell>
              <FolderTableRowCell>
                <div class="max-md:break-all">{{ item.name }}</div>
                <div class="text-sm">
                  Last modified {{ dayjs().to(dayjs(item.updatedAt)) }}
                </div>
                <ExpiryBadge
                  v-if="item.upload.daysToExpiry !== undefined"
                  :time-remaining="item.upload.daysToExpiry"
                  :warning-threshold="10"
                  :time-unit="ExpiryUnitTypes.Days"
                  class="my-2"
                />
              </FolderTableRowCell>
              <FolderTableRowCell v-if="!isMobile">
                <div class="flex justify-between">
                  <div
                    class="flex gap-2 opacity-0 group-hover:!opacity-100 transition-opacity"
                  >
                    <Btn
                      v-if="!item.upload.expired"
                      secondary
                      @click="openModal($event, item)"
                    >
                      <IconDownload class="w-4 h-4" />
                    </Btn>
                    <Btn
                      v-if="!item.upload.expired"
                      data-testid="delete-file"
                      danger
                      @click.stop="
                        openDeleteConfirmation(
                          $event,
                          item.id,
                          item.name,
                          'file'
                        )
                      "
                    >
                      <IconTrash class="w-4 h-4" />
                    </Btn>
                  </div>
                  <Btn class="ml-auto">
                    <IconDotsVertical class="w-4 h-4" />
                  </Btn>
                </div>
              </FolderTableRowCell>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
@import '@send-frontend/apps/common/tbpro-styles.css';
</style>
